'use client';

import { useRef, useCallback, useEffect, useState } from 'react';

import { Plus, Image, Film, FileUp, FileText } from 'lucide-react';
import { Button } from './button';
import { Popover, PopoverItem, PopoverContent } from './popover';

interface AttachmentUploadButtonProps {
	onFilesSelected: (files: FileList) => void;
	disabled?: boolean;
	onCreateNote?: () => void;
	/**
	 * Controlled open state. Composers that collapse on disengagement control
	 * this so they can OR the popover's open state into their `engaged` signal —
	 * otherwise opening the popover (which can blur the editor, especially the
	 * mobile drawer presentation) would re-collapse the composer and kill both
	 * the keyboard and the popover. Omit for plain uncontrolled use.
	 */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

export function AttachmentUploadButton({
	onFilesSelected,
	disabled,
	onCreateNote,
	open: controlledOpen,
	onOpenChange,
}: AttachmentUploadButtonProps) {
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = controlledOpen !== undefined;
	const open = isControlled ? controlledOpen : internalOpen;
	const imageInputRef = useRef<HTMLInputElement>(null);
	const videoInputRef = useRef<HTMLInputElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const setOpen = useCallback(
		(value: boolean) => {
			if (!isControlled) setInternalOpen(value);
			onOpenChange?.(value);
		},
		[isControlled, onOpenChange],
	);

	// Upload items deliberately do NOT close the popover before opening the OS
	// file dialog: the open popover is what keeps a collapsible composer engaged
	// (and therefore expanded and MOUNTED — including these hidden inputs). If we
	// closed first, a pristine composer could collapse and unmount the input
	// while the OS dialog is still open, dropping the user's selection. The
	// popover closes on the input's change/cancel instead.
	const handleImageClick = useCallback(() => {
		imageInputRef.current?.click();
	}, []);

	const handleVideoClick = useCallback(() => {
		videoInputRef.current?.click();
	}, []);

	const handleFileClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleCreateNote = useCallback(() => {
		setOpen(false);
		onCreateNote?.();
	}, [onCreateNote, setOpen]);

	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			onFilesSelected(e.target.files);
		}
		// Reset so the same file can be re-selected
		e.target.value = '';
		setOpen(false);
	}, [onFilesSelected, setOpen]);

	// Close the popover when the OS file dialog is dismissed without a pick
	// (native `cancel` event; React has no typed handler for it on inputs).
	useEffect(() => {
		const inputs = [imageInputRef.current, videoInputRef.current, fileInputRef.current];
		const handleCancel = () => setOpen(false);
		inputs.forEach((input) => input?.addEventListener('cancel', handleCancel));
		return () => inputs.forEach((input) => input?.removeEventListener('cancel', handleCancel));
	}, [setOpen]);

	// Keep focus where it is (the composer's editor) when an item is pressed —
	// on iOS a tap would otherwise blur the editor and drop the keyboard; on
	// desktop the focus move out of the composer card would drop its focus-group
	// engagement. Cancelling mousedown prevents the focus shift; click still fires.
	const keepFocus = useCallback((e: React.MouseEvent) => e.preventDefault(), []);

	const showCreateSection = Boolean(onCreateNote);

	return (
		<>
			<Popover
				position="top"
				align="start"
				offset={8}
				open={open}
				onOpenChange={setOpen}
				content={
					<PopoverContent size="sm" className="w-[200px]">
						<PopoverItem onClick={handleImageClick} onMouseDown={keepFocus} size="list-sm" className="whitespace-nowrap">
							<Image />
							Upload images
						</PopoverItem>
						<PopoverItem onClick={handleVideoClick} onMouseDown={keepFocus} size="list-sm" className="whitespace-nowrap">
							<Film />
							Upload video
						</PopoverItem>
						<PopoverItem onClick={handleFileClick} onMouseDown={keepFocus} size="list-sm" className="whitespace-nowrap">
							<FileUp />
							Upload file
						</PopoverItem>
						{showCreateSection && (
							<>
								<div className="h-px bg-border-muted my-1" />
								{onCreateNote && (
									<PopoverItem onClick={handleCreateNote} onMouseDown={keepFocus} size="list-sm" className="whitespace-nowrap">
										<FileText />
										Create note
									</PopoverItem>
								)}
							</>
						)}
					</PopoverContent>
				}
			>
				<Button
					type="button"
					variant="ghost-muted"
					size="sm"
					iconOnly
					disabled={disabled}
					aria-label="Attach files"
					// Keep the editor focused through the tap (iOS keyboard stays up,
					// the composer's focus-group engagement holds); click still toggles.
					onPointerDown={(e) => e.preventDefault()}
				>
					<Plus strokeWidth={2} />
				</Button>
			</Popover>

			<input
				ref={imageInputRef}
				type="file"
				// The extensions are not redundant: Windows and Linux Chrome hand an
				// iPhone HEIC an empty `File.type`, so `image/*` alone hides it from
				// the picker entirely.
				accept="image/*,.heic,.heif"
				multiple
				className="hidden"
				onChange={handleInputChange}
			/>
			<input
				ref={videoInputRef}
				type="file"
				accept="video/*"
				multiple
				className="hidden"
				onChange={handleInputChange}
			/>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={handleInputChange}
			/>
		</>
	);
}
